import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { toast } from 'react-toastify';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import EntitySearchPicker, { getEntityDisplayName, getEntityId } from '../components/EntitySearchPicker';
import { loadDropdownData, addAddressToEntity, loadEntityAddressItems, getEntityById, loadDevicesTable } from '../customer/onboardingApi';
import { Checkbox, Chip, CircularProgress, InputAdornment, Radio, TextField } from '@mui/material';
import crudsService from 'services/cruds-service';
import StepBar from '../components/StepBar';

// ── Steps ─────────────────────────────────────────────────────
const STEPS = [
    { key: 'search', label: 'Find Entity' },
    { key: 'select-item', label: 'Asset' },
    { key: 'select-device', label: 'Device' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'done', label: 'Done' },
];

// ── Address-item row helpers ──────────────────────────────────
function getItemName(item) {
    return item?.name ?? item?.attributes?.name ?? `Item #${item?.address_item_id ?? item?.id}`;
}

function getItemAddressLine(item) {
    const addr = item?.Address || item?.attributes?.Address;
    if (!addr) return '';
    const parts = [addr.street_address1, addr.city, addr.state].filter(Boolean);
    return parts.join(', ');
}

function getItemAddressLabel(item) {
    const addr = item?.Address || item?.attributes?.Address;
    return addr?.name || '';
}

function getItemId(item) {
    return item?.address_item_id ?? item?.attributes?.address_item_id ?? item?.id ?? 0;
}

function extractItemsFromEntity(entityData) {
    const included = entityData?.data?.included ?? entityData?.included;
    const moduleLocations = Array.isArray(included?.moduleLocations) ? included.moduleLocations : [];
    const rows = [];

    moduleLocations.forEach((loc) => {
        const addr = loc?.Address;
        const modules = Array.isArray(addr?.module) ? addr.module : [];
        modules.forEach((m) => {
            rows.push({
                ...m,
                Address: addr,
            });
        });
    });

    return rows;
}

function getDeviceHardwareLabel(device) {
    return [device.device_type_name, device.brand_name, device.model_name]
        .filter(Boolean)
        .join(' • ');
}

function getDeviceLocationLabel(device) {
    const mappedAsset = [device.map_identifier, device.entity_address_item_name || device.item_name]
        .filter(Boolean)
        .join(' ');
    return [mappedAsset, device.location_label]
        .filter(Boolean)
        .join(' • ');
}

function getDeviceTelemetryLabel(device) {
    const level = typeof device.percent_level === 'number' ? `Level ${device.percent_level}%` : '';
    return [
        device.last_reported_at ? `Reported ${device.last_reported_at}` : '',
        device.battery_status ? `Battery ${device.battery_status}` : '',
        level,
        device.trend ? `Trend ${device.trend}` : '',
        device.usage_status || '',
    ]
        .filter(Boolean)
        .join(' • ');
}

export default function AssignDeviceWizard() {
    const [step, setStep] = useState('search');
    const [selectedEntity, setSelectedEntity] = useState(null);
    const [addressItems, setAddressItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemFilter, setItemFilter] = useState('');

    const [devices, setDevices] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [deviceFilter, setDeviceFilter] = useState('');
    const [showAssigned, setShowAssigned] = useState(false);

    const [submitting, setSubmitting] = useState(false);

    // Load entity address items when entity selected
    const loadItems = async (entity) => {
        setLoadingItems(true);
        try {
            const entityId = getEntityId(entity);
            const data = await loadEntityAddressItems(entityId, {
                page_size: 200,
                fetchAll: true,
            });
            const rows = data?.data ?? data ?? [];
            let normalizedRows = Array.isArray(rows) ? rows : [];

            if (normalizedRows.length === 0) {
                const entityData = await getEntityById(entityId);
                normalizedRows = extractItemsFromEntity(entityData);
            }
            setAddressItems(normalizedRows);
        } catch {
            toast.error('Failed to load address items');
            setAddressItems([]);
        } finally {
            setLoadingItems(false);
        }
    };

    // Load devices table on demand
    const loadDevices = async (force = false) => {
        if (!force && devices.length > 0) return; // already loaded
        setLoadingDevices(true);
        try {
            const rows = await loadDevicesTable();
            setDevices(rows);
        } catch {
            toast.error('Failed to load devices');
        } finally {
            setLoadingDevices(false);
        }
    };

    const handleEntitySelect = (entity) => {
        setSelectedEntity(entity);
        setSelectedItem(null);
        setAddressItems([]);
        setItemFilter('');
        if (entity) {
            loadItems(entity);
        }
    };

    const handleProceedToDevice = () => {
        setStep('select-device');
        loadDevices(true);
    };

    const handleSubmit = async () => {
        if (!selectedItem || !selectedDevice || !selectedEntity) return;
        setSubmitting(true);
        try {
            // const entityId = getEntityId(selectedEntity);
            const itemId = getItemId(selectedItem);
            await crudsService.reAssginDevice(selectedDevice.device_id, itemId);
            setStep('done');
            toast.success('Device assigned successfully!');
        } catch (err) {
            toast.error(err.message || 'Failed to assign device');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredItems = addressItems.filter(item => {
        if (!itemFilter) return true;
        const f = itemFilter.toLowerCase();
        return (
            getItemName(item).toLowerCase().includes(f) ||
            getItemAddressLine(item).toLowerCase().includes(f) ||
            getItemAddressLabel(item).toLowerCase().includes(f)
        );
    });

    const filteredDevices = devices.filter(d => {
        const isUnassigned = !d.address_item_id;
        if (!showAssigned && !isUnassigned) return false;
        if (!deviceFilter) return true;
        const f = deviceFilter.toLowerCase();
        return (
            d.device_name.toLowerCase().includes(f) ||
            (d.serial_number ?? '').toLowerCase().includes(f) ||
            (d.external_device_id ?? '').toLowerCase().includes(f) ||
            (d.device_type_name ?? '').toLowerCase().includes(f) ||
            (d.brand_name ?? '').toLowerCase().includes(f) ||
            (d.model_name ?? '').toLowerCase().includes(f) ||
            (d.map_identifier ?? '').toLowerCase().includes(f) ||
            (d.item_name ?? '').toLowerCase().includes(f) ||
            (d.entity_address_item_name ?? '').toLowerCase().includes(f)
        );
    });

    return (
        <MDBox sx={{ mx: 'auto' }}>
            {step !== 'done' && <StepBar current={step} STEPS={STEPS} />}

            {/* ── Step 1: Search entity ── */}
            {step === 'search' && (
                <MDBox sx={{ backgroundColor: 'rgb(38, 40, 51)', border: '1px solid grey', borderRadius: '12px', p: 3 }}>
                    <MDTypography variant="h6" color="white" fontWeight="bold">Find the Entity</MDTypography>
                    <MDTypography variant="button" color="white" opacity={0.6} sx={{ display: 'block', mb: 3 }}>
                        Search for the customer or entity you want to add an address to.
                    </MDTypography>

                    <EntitySearchPicker
                        selectedEntity={selectedEntity}
                        onSelect={handleEntitySelect}
                    />

                    <MDBox mt={4} display="flex" justifyContent="flex-end">
                        <MDButton
                            disabled={!selectedEntity}
                            onClick={() => setStep('select-item')}
                            variant="gradient"
                            color="info"
                            sx={{ gap: 1 }}
                        >
                            Continue <i className="fa fa-chevron-right" />
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 2: Address form ── */}
            {step === "select-item" && selectedEntity && (
                <MDBox
                    sx={{
                        backgroundColor: "rgb(38, 40, 51)",
                        border: "1px solid grey",
                        borderRadius: 3,
                        p: 3,
                    }}
                >
                    <MDTypography
                        variant="h6"
                        sx={{ color: "white", display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <i className="fas fa-layer-group" style={{ color: "#a855f7" }} />
                        Select Asset / Item
                    </MDTypography>

                    <MDTypography variant="body2" sx={{ color: "gray", mb: 3 }}>
                        Choose the item to assign a device to for{" "}
                        <strong style={{ color: "white" }}>
                            {getEntityDisplayName(selectedEntity)}
                        </strong>
                    </MDTypography>

                    {addressItems.length > 5 && (
                        <TextField
                            fullWidth
                            size="small"
                            value={itemFilter}
                            onChange={(e) => setItemFilter(e.target.value)}
                            placeholder="Filter items…"
                            sx={{
                                mb: 2,
                                input: { color: "white" },
                                fieldset: { borderColor: "#49a3f1" },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <i className="fas fa-search" style={{ color: "gray" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    {loadingItems ? (
                        <MDBox sx={{ textAlign: "center", py: 4 }}>
                            <CircularProgress size={22} sx={{ color: "#49a3f1" }} />
                        </MDBox>
                    ) : (
                        <MDBox sx={{ maxHeight: 320, overflowY: "auto" }}>
                            {filteredItems.map((item) => {
                                const id = getItemId(item);
                                const isSelected =
                                    selectedItem && getItemId(selectedItem) === id;

                                return (
                                    <MDBox
                                        key={id}
                                        onClick={() => setSelectedItem(item)}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            p: 2,
                                            mb: 1,
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            border: "1px solid",
                                            borderColor: isSelected ? "#49a3f1" : "gray",
                                            backgroundColor: isSelected ? "transparent" : "transparent",
                                            "&:hover": { borderColor: "#49a3f1" },
                                        }}
                                    >
                                        <Radio
                                            checked={isSelected}
                                            sx={{ color: "#49a3f1" }}
                                        />

                                        <MDBox sx={{ flex: 1 }}>
                                            <MDTypography sx={{ color: "white", fontSize: 14 }}>
                                                {getItemName(item)}
                                            </MDTypography>
                                            <MDTypography
                                                variant="caption"
                                                sx={{ color: "white" }}
                                            >
                                                {getItemAddressLine(item)}
                                            </MDTypography>
                                        </MDBox>

                                        {item?.Device && (
                                            <Chip
                                                label="Has device"
                                                size="small"
                                                sx={{
                                                    backgroundColor: "rgba(255,165,0,0.2)",
                                                    color: "orange",
                                                    border: "1px solid rgba(255,165,0,0.3)",
                                                }}
                                            />
                                        )}
                                    </MDBox>
                                );
                            })}
                        </MDBox>
                    )}

                    <MDBox sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => setStep("search")}
                            startIcon={<i className="fas fa-chevron-left" />}
                        >
                            Back
                        </MDButton>

                        <MDButton
                            disabled={!selectedItem}
                            onClick={handleProceedToDevice}
                            variant="gradient"
                            color="info"
                            endIcon={<i className="fas fa-chevron-right" />}
                        >
                            Select Device
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 3: Review ── */}
            {step === "select-device" && selectedItem && (
                <MDBox sx={{ backgroundColor: "rgb(38, 40, 51)", border: "1px solid grey", borderRadius: 3, p: 3 }}>
                    <MDTypography variant="h6" sx={{ color: "white", display: "flex", gap: 1, alignItems: 'center' }}>
                        <i className="fas fa-microchip" style={{ color: "orange" }} />
                        Select Device
                    </MDTypography>

                    <TextField
                        fullWidth
                        size="small"
                        value={deviceFilter}
                        onChange={(e) => setDeviceFilter(e.target.value)}
                        placeholder="Search by name or serial…"
                        sx={{ my: 2, input: { color: "white" } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <i className="fas fa-search" style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <MDBox sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Checkbox
                            checked={showAssigned}
                            onChange={(e) => setShowAssigned(e.target.checked)}
                            sx={{ color: "#49a3f1" }}
                        />
                        <MDTypography variant="caption" sx={{ color: "gray" }}>
                            Show assigned
                        </MDTypography>
                    </MDBox>

                    <MDBox sx={{ maxHeight: 320, overflowY: "auto" }}>
                        {filteredDevices.map((device) => {
                            const isSelected =
                                selectedDevice?.device_id === device.device_id;

                            return (
                                <MDBox
                                    key={device.device_id}
                                    onClick={() => setSelectedDevice(device)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 2,
                                        mb: 1,
                                        borderRadius: 2,
                                        cursor: "pointer",
                                        border: "1px solid",
                                        borderColor: isSelected ? "#49a3f1" : "gray",
                                        backgroundColor: isSelected ? "transparent" : "transparent",
                                        "&:hover": { borderColor: "#49a3f1" },
                                    }}
                                >
                                    <Radio checked={isSelected} sx={{ color: "#49a3f1" }} />

                                    <i
                                        className="fas fa-microchip"
                                        style={{
                                            marginRight: 10,
                                            color: device.address_item_id
                                                ? "orange"
                                                : "#49a3f1",
                                        }}
                                    />

                                    <MDBox sx={{ flex: 1 }}>
                                        <MDTypography sx={{ color: "white", fontSize: 14 }}>
                                            {device.device_name} · #{device.device_id}
                                        </MDTypography>

                                        <MDTypography variant="caption" sx={{ color: "gray" }}>
                                            {device.address_item_id
                                                ? `Assigned to item #${device.address_item_id}`
                                                : "Unassigned"}
                                        </MDTypography>
                                    </MDBox>

                                    {device.address_item_id && (
                                        <Chip
                                            label="Assigned"
                                            size="small"
                                            sx={{
                                                backgroundColor: "rgba(255,165,0,0.2)",
                                                color: "orange",
                                            }}
                                        />
                                    )}
                                </MDBox>
                            );
                        })}
                    </MDBox>

                    <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => setStep('select-item')}
                            startIcon={<i className="fas fa-chevron-left" />}
                        >
                            Back
                        </MDButton>

                        <MDButton
                            variant="gradient"
                            color="info"
                            disabled={!selectedDevice}
                            onClick={() => setStep('confirm')}
                            endIcon={<i className="fas fa-chevron-right" />}
                        >
                            Review
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 4: Done ── */}
            {step === "confirm" && (
                <MDBox sx={{ backgroundColor: "rgb(38, 40, 51)", border: "1px solid grey", borderRadius: 3, p: 3 }}>
                    <MDTypography variant="h6" sx={{ color: "white", mb: 3 }}>
                        Confirm Assignment
                    </MDTypography>

                    <MDBox sx={{ mb: 3 }}>
                        <MDTypography sx={{ color: "white", fontSize: 12 }}>Device</MDTypography>
                        <MDTypography sx={{ color: "white" }}>
                            {selectedDevice.device_name}
                        </MDTypography>
                    </MDBox>

                    <MDBox sx={{ display: "flex", justifyContent: "space-between" }}>
                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => setStep("select-device")}
                            startIcon={<i className="fas fa-chevron-left" />}
                        >
                            Change Device
                        </MDButton>

                        <MDButton
                            variant="gradient"
                            color="info"
                            onClick={handleSubmit}
                            disabled={submitting}
                            endIcon={<i className="fas fa-chevron-right" />}
                        >
                            {submitting ? "Assigning..." : "Assign Device"}
                        </MDButton>
                    </MDBox>
                </MDBox>
            )}

            {/* ── Step 5: Done ── */}
            {step === "done" && (
                <MDBox
                    sx={{
                        backgroundColor: "rgb(38, 40, 51)",
                        border: "1px solid rgba(74,222,128,0.4)",
                        borderRadius: 3,
                        p: 5,
                        textAlign: "center",
                    }}
                >
                    <i
                        className="fas fa-check-circle"
                        style={{ fontSize: 48, color: "#4ADE80" }}
                    />

                    <MDTypography variant="h5" sx={{ color: "white", mt: 2 }}>
                        Device Assigned!
                    </MDTypography>

                    <MDTypography sx={{ color: "gray", mt: 1 }}>
                        {selectedDevice.device_name} has been assigned successfully.
                    </MDTypography>
                </MDBox>
            )}
        </MDBox>
    );
}
